import { map, catchError } from 'rxjs/operators';
import { Injectable, signal, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { SessionStorageService } from '../services/session-storage.service';
import { EnvironmentService } from '../services/environment.service';
import { ErrorReportingService } from '../services/error-reporting.service';
import { Observable } from 'rxjs';
import {
  GridSetupResponse,
  GridDataResponse,
  PostTradeDetailResponse,
  TradeGridSummaryResponse,
  UserReportsListResponse,
  AiSummaryResponse,
  AiSummarySearchResponse,
  TradeResponse,
  RepDetail,
  PostTradeGridSetup,
  AccountInfo,
  AmlDetail,
  AccountLinkDetail,
  AccountResult,
  HashResult,
  TradeSourceData,
  ItemUpdate,
  SearchAiSummary,
  ShellAccountDetail,
  TrueRule,
} from '../models/post-trade-models';
import { StringResult } from '../models/general-models';

@Injectable({ providedIn: 'root' })
export class PostTradeService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private envSvc = inject(EnvironmentService);
  private errSvc = inject(ErrorReportingService);

  private environment;
  public baseUrl: string;
  public f5500BaseUrl: string;
  private org: string;
  loading = signal(false);

  constructor() {
    this.environment = this.envSvc.get();

    if (!this.environment) {
      this.envSvc.loadSettings();
      this.environment = this.envSvc.get();
    }

    this.f5500BaseUrl = this.environment.f5500Base;
    this.baseUrl = this.environment.apiBase;
    this.org = this.environment.org;
    this.org = this.environment.org || 'basev2';
  }

  getPostGridSetup(): Observable<GridSetupResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/TradeGridSetup?firmid=a`;

    return this.http.get<GridSetupResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getPostTradeGridData(queryData?: string): Observable<GridDataResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    let url = this.baseUrl + `/PostTrade/TradeGrid`;

    if (queryData) {
      url = url + `?query=${queryData}`;
    }

    return this.http.get<GridDataResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  generatePostTradeReviewReport(
    gridType: string,
    fileName: string,
    query: string,
    columns: string,
    rows: string,
    category: string
  ): Observable<StringResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    let endpoint;
    if (gridType.toLowerCase() === 'account') {
      endpoint = '/Account/AccountGridReport';
    } else if (gridType.toLowerCase() === 'aml') {
      endpoint = '/AML/AMLGridReport';
    } else {
      endpoint = `/PostTrade/${gridType}GridReport`;
    }
    const url =
      this.baseUrl +
      `${endpoint}?fileName=${fileName}&query=${query}&columns=${columns}&maxRows=${rows}&category=${category} Monitor`;

    return this.http.get<StringResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  generateAuditReport(
    fileName: string,
    query: string,
    columns: string,
    category: string
  ): Observable<StringResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url =
      this.baseUrl +
      `/PostTrade/TradeAuditReport?fileName=${fileName}&query=${query}&columns=${columns}&maxRows=1000&category=${category} Monitor`;

    return this.http.get<StringResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getPostTradeDetail(id: string): Observable<PostTradeDetailResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/Detail?id=${id}`;

    return this.http
      .get<PostTradeDetailResponse>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getTradeGridSummary(): Observable<TradeGridSummaryResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/TradeGridSummary`;

    return this.http
      .get<TradeGridSummaryResponse>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  updateTrade(data: ItemUpdate): Observable<TradeResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/UpdateTradeRecord`;

    return this.http
      .post<TradeResponse>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getAccountGridSetup(): Observable<GridSetupResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/Account/GridSetup?firmid=a`;

    return this.http.get<GridSetupResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getAccountGridData(queryData: string): Observable<GridDataResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    let url = this.baseUrl + `/Account/AccountGrid`;
    if (queryData) {
      url = url + `?query=${queryData}`;
    }
    return this.http.get<GridDataResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getAccountDetail(id: string): Observable<PostTradeDetailResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/Account/Detail?accountID_rev=${id}`;

    return this.http
      .get<PostTradeDetailResponse>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getAccountGridSummary(): Observable<TradeGridSummaryResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/Account/AccountGridSummary`;

    return this.http
      .get<TradeGridSummaryResponse>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  updateAccount(data: ItemUpdate): Observable<TradeResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/Account/UpdateAccount`;

    return this.http
      .post<TradeResponse>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getUserReportsList(): Observable<UserReportsListResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/UserReportsList`;

    return this.http
      .get<UserReportsListResponse>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  downloadUserReport(id: string): Observable<Blob> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('accept', 'application/zip');
    const url = this.baseUrl + `/PostTrade/GetFile?ID=${id}`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body as Blob;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getRepGridSetup(): Observable<GridSetupResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/RepGridSetup?firmid=a`;

    return this.http.get<GridSetupResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getRepGridData(queryData: string): Observable<GridDataResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    let url = this.baseUrl + `/PostTrade/RepGrid`;
    if (queryData) {
      url = url + `?query=${queryData}`;
    }
    return this.http.get<GridDataResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  updateRep(data: ItemUpdate): Observable<TradeResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/UpdateRepRecord`;

    return this.http
      .post<TradeResponse>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getRepDetail(id: string): Observable<RepDetail> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/RepDetail?id=${id}`;

    return this.http.get<RepDetail>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getRepGridSummary(): Observable<TradeGridSummaryResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/RepGridSummary`;

    return this.http
      .get<TradeGridSummaryResponse>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getAccountGroupingGridData(queryData?: any): Observable<GridDataResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/AccountGroup/SearchGroup`;

    return this.http
      .post<GridDataResponse>(url, queryData, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getAccountGroupingDetail(id: string): Observable<AccountInfo[]> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/AccountGroup/Details?GroupID=${id}`;

    return this.http.get<AccountInfo[]>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  createAccountGroup(accountData: {
    description: string;
    unitid: string;
  }): Observable<AccountResult> {
    const { description, unitid } = accountData;
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl +
      `/AccountGroup/Create?description=${description}&unitid=${unitid}&type=BPHH`;

    return this.http.get<AccountResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  deleteAccountGroup(id: string): Observable<AccountResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/AccountGroup/Delete?GroupID=${id}`;

    return this.http.delete<AccountResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  searchAccount(data: any): Observable<AccountInfo[]> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url = this.baseUrl + `/AccountGroup/Search?`;

    // if (data) {
    //   url += `search=${data}`;
    // }

    return this.http
      .post<AccountInfo[]>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  linkAccount(groupId: string, accountId: string): Observable<AccountResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url =
      this.baseUrl +
      `/AccountGroup/LinkAccount?GroupID=${groupId}&AccountID=${accountId}`;

    return this.http.get<AccountResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  makePrimaryAccount(
    groupId: string,
    accountId: string
  ): Observable<AccountResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url =
      this.baseUrl +
      `/AccountGroup/AddPrimaryGroup?GroupID=${groupId}&AccountID=${accountId}`;

    return this.http.get<AccountResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  removeAccount(groupId: string, accountId: string): Observable<AccountResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl +
      `/AccountGroup/RemoveAccount?GroupID=${groupId}&AccountID=${accountId}`;

    return this.http.delete<AccountResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getAmlGridSetup(): Observable<GridSetupResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/AML/AlertGridSetup?firmid=a`;

    return this.http.get<GridSetupResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getAlGridSetup(): Observable<PostTradeGridSetup> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/AccountLinking/AlertGridSetup`;
    return this.http.get<PostTradeGridSetup>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }
  getAccountLinkingGridData(queryData?: any): Observable<GridDataResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/AccountLinking/GridDetails?query=${queryData}`;
    return this.http.get<GridDataResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }
  getLinkedAccountRecord(id: string) {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl +
      `/AccountLinking/LinkedAccountRecord?DirectAccountLinkID=${id}`;
    return this.http.get<AmlDetail>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }
  getAccountLinkingDetails(id: string) {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl +
      `/AccountLinking/DirectAccountLinkDetails?DirectAccountLinkID=${id}`;
    return this.http.get<AccountLinkDetail>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }
  updateLinkedAccount(data) {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/AccountLinking/UpdateLink`;
    return this.http.patch<void>(url, data, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return this.errSvc.handleError(error);
      })
    );
  }
  getSearchAccountSetup(): Observable<GridSetupResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/AccountLinking/SearchAccountSetup`;
    return this.http.get<GridSetupResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }
  getSearchAccountGridData(queryData?: any): Observable<GridDataResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl + `/AccountLinking/AccountSearch?query=${queryData}`;
    return this.http.get<GridDataResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }
  getShellAccountDetails(id: string) {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl + `/AccountLinking/ShellAccountDetails?ShellAccountID=${id}`;
    return this.http.get<ShellAccountDetail>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getAmlGridData(queryData: string): Observable<GridDataResponse> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    let url = this.baseUrl + `/AML/AlertGridDetails`;
    if (queryData) {
      url = url + `?query=${queryData}`;
    }
    return this.http.get<GridDataResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getAmlDetail(id: string): Observable<AmlDetail> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/AML/Details/${id}`;

    return this.http.get<AmlDetail>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  updateAmlRecord(data: ItemUpdate): Observable<AccountResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/AML/UpdateAlertRecord`;

    return this.http
      .post<AccountResult>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getHashString(stringValue: string): Observable<HashResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/PostTrade/Hash`;

    const data = {
      targetString: stringValue,
      useStaticSalt: true,
    };

    return this.http.post<HashResult>(url, data, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  aiBulkProcess(data: {
    type: string;
    profileids: string[];
    promptname: string;
    group: string;
  }): Observable<AiSummaryResponse> {
    // generate ai summary
    const { type, profileids, promptname, group } = data;
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    let url =
      this.baseUrl + `/AI/AiBulkProcess?type=${type}&profileids=${profileids}`;

    if (promptname) {
      url += `&promptname=${promptname}`;
    }
    if (group) {
      url += `&group=${group}`;
    }

    return this.http
      .post<AiSummaryResponse>(url, null, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getAiSummarySearch(
    payloadData: SearchAiSummary
  ): Observable<AiSummarySearchResponse> {
    // search for ai summaries
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/Profile/Text/Search`;

    return this.http
      .post<AiSummarySearchResponse>(url, payloadData, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getAiSummary(
    tradeId: string,
    summaryType = 'trade'
  ): Observable<AiSummaryResponse> {
    // get summary (for pretrade: tradeId === profileId and summaryType === 'aicasereview')
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url = this.baseUrl + `/Profile/Text/${tradeId}?type=${summaryType}`;

    return this.http.get<AiSummaryResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  patchAiSummary(data: {
    text: string;
    referenceId: string;
    type: string;
  }): Observable<void> {
    const { text, referenceId, type } = data;
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Profile/Text?referenceId=${referenceId}&type=${type}`;

    return this.http.patch<void>(url, text, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  viewSourceData(tradeId: string): Observable<TradeSourceData> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url = this.baseUrl + `/PostTrade/RawSourceData?tradeId=${tradeId}`;

    return this.http.get<TradeSourceData>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  bridgeHasAlerts(trueRules: TrueRule[]): boolean {
    const alerts = trueRules.filter(
      rule => rule.headline && rule.headline !== ''
    );

    return alerts.length > 0;
  }
}
